
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { UseFormReturn } from "react-hook-form";
import { ProjectFormValues } from "./types";
import { useEffect } from "react";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Contact } from "@/types/contacts";

interface ContactSelectorProps {
  form: UseFormReturn<ProjectFormValues>;
}

export const ContactSelector = ({ form }: ContactSelectorProps) => {
  const { currentOrganization } = useOrganization();
  
  const { data, isLoading, error } = useQuery({
    queryKey: ["contacts-for-projects", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) return [];
      
      // Direct database query - much faster than edge function
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('name', { ascending: true })
        .limit(100);
      
      if (error) {
        console.error("Error fetching contacts:", error);
        throw error;
      }
      
      return data || [];
    },
    enabled: !!currentOrganization,
  });

  // Set initial value when contacts are loaded and there are contact_ids
  useEffect(() => {
    const initialContactIds = form.getValues("contact_ids");
    if (data && data.length > 0 && initialContactIds && initialContactIds.length > 0) {
      // Ensure the contact exists in our loaded contacts
      const contactExists = initialContactIds.some((id: string) => 
        data.some((c: Contact) => c.id === id)
      );
      
      if (contactExists) {
        console.log("Setting initial contact IDs:", initialContactIds);
        form.setValue("contact_ids", initialContactIds);
      }
    }
  }, [data, form]);

  if (!currentOrganization) {
    return null;
  }

  // Get the contact ID from the form
  const contactId = form.watch("contact_ids")?.[0];
  const selectedContact = contactId ? data?.find((c: Contact) => c.id === contactId) : null;

  return (
    <FormField
      control={form.control}
      name="contact_ids"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Project Contact</FormLabel>
          <Select
            disabled={isLoading}
            value={field.value?.[0] || ""}
            onValueChange={(value) => {
              form.setValue("contact_ids", value && value !== "none" ? [value] : []);
            }}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue 
                  placeholder={isLoading ? "Loading contacts..." : "Select a contact"}
                >
                  {selectedContact?.name || "Select a contact"}
                </SelectValue>
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {data && data.map((contact: Contact) => (
                <SelectItem key={contact.id} value={contact.id}>
                  {contact.name} {contact.company ? `(${contact.company})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
